import React, { FC, useCallback, useEffect, useState } from 'react'

import { ModalAPI } from '../StoryShareModel'
import { CompleteModal } from '../CompleteModal'
import { useShareStoryToGhMutation } from './shareToGlobalHub.types'
import { FeedResponse } from 'shared/__gql-types'
import { useGetUsersCountQuery } from 'modulesCommon/graphql/queries/getUsersCount.types'
import { GET_FEEDS_BY_CAMPAIGN_ID } from 'modules/Feed/graphql/queries'
import { DEFAULT_ENTITIES_LIMIT } from '../../../../shared/consts'
import { useReactiveVar } from '@apollo/client'
import { countryCodeVar } from '../../../../modulesCommon/store'
import { TStory } from '../../types'

type UseShareStory = [
  React.ReactNode,
  () => void,
]
const RunningCounter: FC<{ count: number }> = ({ count = 0 }) => {
  const [currentCount, setCount] = useState<number>(0)
  useEffect(() => {
    let increment = 0
    const diff = count - currentCount
    if (diff > 1000) increment = 1000
    else if (diff > 100) increment = 100
    else if (diff > 10) increment = 10
    else increment = 1


    if (currentCount <= count) {
      const timeout = setTimeout(() => {
        setCount(prevCount => prevCount + increment)
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [count, currentCount])
  return <span style={{ fontFamily: 'monospace' }}>{currentCount}</span>
}
export const useShareStoryToGlobalhub = (story: TStory, handleModal?: (api: ModalAPI) => () => void): UseShareStory => {
  const { data } = useGetUsersCountQuery()
  const country = useReactiveVar(countryCodeVar)
  const usersCount = parseInt(data?.listUsers?.total ?? '0')
  const [shareMutation] = useShareStoryToGhMutation()
  const [isOpen, setModalStatus] = useState<boolean>(false)


  const handleShare = useCallback(
    (message: string) => shareMutation({
      variables: {
        storyId: story.id,
        comment: message
      },
      update(cache, { data }, { variables }) {
        if (typeof data?.makeStoryGlobal !== 'undefined') {
          const variables = {
            campaignId: story.campaign_id,
            limit: DEFAULT_ENTITIES_LIMIT,
            offset: 0,
            country
          }
          const res = cache.readQuery<{ getFeedsByCampaignId: FeedResponse }>({
            query: GET_FEEDS_BY_CAMPAIGN_ID,
            variables
          })

          cache.writeQuery({
            query: GET_FEEDS_BY_CAMPAIGN_ID,
            variables,
            data: {
              getFeedsByCampaignId: {
                ...res?.getFeedsByCampaignId,
                feeds: [...res?.getFeedsByCampaignId?.feeds?.map((s) => s?.id === story.id ? {
                  ...s,
                  review_status: data?.makeStoryGlobal?.review_status,
                  global_status: data?.makeStoryGlobal?.global_status
                } : s) ?? []]
              }
            }
          })
        }
      }
    }).then(() => {
      setModalStatus(true)
    }),
    [shareMutation, story.id, story.campaign_id, country]
  )

  const handleAction = useCallback(
    () => {
      if (typeof handleModal === 'function') {
        const openModal = handleModal({
          title: 'Share with the Global Hub and Affiliate Zones!',
          description: (
            <p>Please confirm whether you would like to share this story. By sharing, it has the <b>potential to be seen
              by <RunningCounter count={usersCount} /> users</b> in the Global Hub and affiliate regions where your
              project
              has been localized.</p>),
          onShare: handleShare
        })
        openModal()
      }
    },
    [handleModal, handleShare, usersCount]
  )

  return [
    (
      <CompleteModal
        key="complete-modal"
        title="Impressive Milestone!"
        description={
          <span>Your story just made a huge splash across Story Box and the Global Hub, with over <b>{usersCount} users</b> tuning in to hear your message.</span>}
        isOpen={isOpen}
        onClose={() => setModalStatus(false)}
      />
    ),
    handleAction
  ]
}
